import { useState } from 'react';
import { Send, Check, Trash2, Inbox } from 'lucide-react';
import { useCollection } from '../lib/hooks';
import { submitRequest, errorText } from '../lib/api';
import { pb, formatWhen, type AuthUser } from '../lib/pb';
import type { RecordModel } from 'pocketbase';
import { confirmDialog } from './ConfirmDialog';

interface RequestRecord extends RecordModel {
  user: string;
  subject: string;
  message: string;
  image_url: string;
  done: boolean;
}

/**
 * Requests & Suggestions.
 *
 * v1 kept these in a `requests.json` file written with a synchronous
 * read-modify-write, which lost entries under concurrency and was wiped on every
 * container redeploy. They're records now.
 */
export default function RequestsAndSuggestions({
  user,
  isAdmin,
}: {
  user: AuthUser;
  isAdmin: boolean;
}) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Collection rules limit non-admins to their own rows, so this is the same
  // query for everyone — the server decides what comes back.
  const requests = useCollection<RequestRecord>('requests', { sort: '-created' });

  const send = async () => {
    if (!subject.trim() || !message.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await submitRequest(subject.trim(), message.trim());
      setSubject('');
      setMessage('');
      setSent(true);
      setTimeout(() => setSent(false), 4000);
      await requests.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6 items-start">
      <div className="zen-card p-6 space-y-4">
        <div>
          <h2 className="font-serif text-lg font-medium">Request something</h2>
          <p className="text-xs text-natural-text/55 mt-0.5">
            A new influencer, a fix, an idea — anything.
          </p>
        </div>

        <div>
          <label className="zen-label">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Short summary"
            className="zen-input"
          />
        </div>

        <div>
          <label className="zen-label">Details</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={7}
            placeholder="As much detail as you can give"
            className="zen-input text-sm resize-y"
          />
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <button
          onClick={send}
          disabled={busy || !subject.trim() || !message.trim()}
          className="zen-btn-primary w-full"
        >
          {sent ? <Check size={15} /> : <Send size={15} />}
          {busy ? 'Sending…' : sent ? 'Sent — thank you' : 'Send'}
        </button>
      </div>

      <div className="zen-card p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Inbox size={16} className="text-natural-text/40" />
          <h2 className="font-serif text-lg font-medium">
            {isAdmin ? 'All submissions' : 'Your submissions'}
          </h2>
        </div>

        {requests.loading && <p className="text-xs text-natural-text/40">Loading…</p>}
        {!requests.loading && requests.items.length === 0 && (
          <p className="text-xs text-natural-text/40 py-6 text-center">Nothing here yet.</p>
        )}

        <div className="space-y-3 max-h-[520px] overflow-y-auto -mr-2 pr-2">
          {requests.items.map((r) => (
            <div
              key={r.id}
              className={`p-4 rounded-2xl border text-xs ${
                r.done ? 'border-natural-border bg-stone-50 opacity-70' : 'border-natural-border'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <p className="font-semibold">{r.subject}</p>
                <span className="text-[10px] text-natural-text/40 shrink-0">
                  {formatWhen(r.created)}
                </span>
              </div>

              <p className="text-natural-text/70 leading-relaxed whitespace-pre-wrap break-words">
                {r.message}
              </p>

              {isAdmin && (
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-natural-border/60">
                  <button
                    onClick={async () => {
                      await pb.collection('requests').update(r.id, { done: !r.done });
                      await requests.reload();
                    }}
                    className="zen-btn-ghost py-1 px-2 text-[10px]"
                  >
                    <Check size={11} />
                    {r.done ? 'Reopen' : 'Mark done'}
                  </button>
                  <button
                    onClick={async () => {
                      if (
                        !(await confirmDialog({
                          title: 'Delete this submission?',
                          message: 'It will be removed for everyone.',
                          confirmLabel: 'Delete',
                          danger: true,
                        }))
                      ) {
                        return;
                      }
                      await pb.collection('requests').delete(r.id);
                      await requests.reload();
                    }}
                    className="zen-btn-danger py-1 px-2 text-[10px]"
                  >
                    <Trash2 size={11} />
                  </button>
                  <span className="ml-auto text-[10px] text-natural-text/40 font-mono">
                    {r.user === user.id ? 'you' : r.user.slice(0, 8)}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
